// command-adapter-table.ts — the 13 adapter DATA rows (spec §9.1; 11 M0 rows
// plus review-clean-v1 + testaudit-v1 from §C2),
// mirroring team-ai-pack workflows/autopilot/command-adapters.md exactly.
// Split out of command-adapter.ts (validation logic) purely to keep both
// files under the 200-line project limit — this file has no logic, only
// the table. Types + shared tool-list fragments live in
// command-adapter-types-and-tool-fragments.ts; the review/controller rows in
// command-adapter-review-and-controller-rows.ts (plan 20260924-0205). Every
// name that used to be exported from here still is.
import { REVIEW_AND_CONTROLLER_ADAPTER_ROWS } from "./command-adapter-review-and-controller-rows.js";
import {
  type CommandAdapterDefinition,
  EDIT_FAMILY_TOOLS,
  READ_ONLY_TOOLS,
  STAGING_WRITE_TOOLS,
} from "./command-adapter-types-and-tool-fragments.js";

export type {
  CommandAdapterDefinition,
  CommandAdapterExecutor,
  CommandAdapterKind,
} from "./command-adapter-types-and-tool-fragments.js";
export { EDIT_FAMILY, STAGING_WRITE_TOOLS } from "./command-adapter-types-and-tool-fragments.js";

/**
 * Adapters whose attempt MUST declare `evidence[]` (audit 2026-09-14 finding 2).
 *
 * These are the four that exist to run a verification recipe and report its
 * result: the two that write code plus tests (`testcase-v1`, `implement-v1`),
 * the one that repairs it (`fix-v1`), and the one whose whole job is running
 * the suite (`test-v1`). An attempt from any of them with `evidence: []` is
 * claiming "done" with nothing to show, which is exactly what let a mission
 * report six tickets `done` while the real suite exited 1.
 *
 * Declared EXPLICITLY rather than derived from `allowedTools`, and the reason
 * matters: `survey-v1` also carries `Bash(npm test*)` + `Bash(pytest*)` (it may
 * run a suite to observe a brownfield baseline), so "has a test command" does
 * NOT separate the two groups. A derivation on that property would silently
 * pull `survey-v1` in and break every survey attempt. The set is pinned by
 * evidence-required-for-code-adapters.test.ts so adding an adapter to the table
 * without deciding this question turns that test red instead of drifting.
 *
 * Keyed by ADAPTER ID, never by `ticket.kind`: `kind` is worker-proposable
 * (finding 1), `command_adapter` is controller-assigned.
 */
export const EVIDENCE_REQUIRED_ADAPTER_IDS: readonly string[] = [
  "testcase-v1",
  "implement-v1",
  "test-v1",
  "fix-v1",
];

export const RAW_ADAPTERS: CommandAdapterDefinition[] = [
  {
    id: "survey-v1",
    kind: "survey",
    executor: "claude",
    entryPoint: "CLI prompt (run-command-prompt-builder) + workflows/autopilot/survey.md",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} status*)",
      "Bash(git -C {{WORKTREE}} diff*)",
      "Bash(npm test*)",
      "Bash(pytest*)",
      // N-05 (dogfood worker thật 2026-09-15): survey.md steps 3/7 require
      // CURRENT_STATE.md + the gap-analysis draft to be WRITTEN into staging,
      // and every other staging-producing adapter (plan/decision/review-local)
      // carries this exact scoped grant — survey-v1 alone did not, so 3/3 real
      // survey attempts hit TOOL_NOT_ALLOWED and blocked/escalated. Scoped to
      // staging only: tracked source stays read-only (no bare Edit-family
      // name, so B29 and resolveReadOnlySurveyAdapter are unaffected).
      ...STAGING_WRITE_TOOLS,
    ],
    maxInternalSubagents: 1,
    agentHints: [],
    skillHints: [],
    playbook: "workflows/autopilot/survey.md",
  },
  {
    id: "plan-v1",
    kind: "plan",
    executor: "claude",
    entryPoint: "CLI prompt (run-command-prompt-builder) + kind playbook (agent planner)",
    allowedTools: [...READ_ONLY_TOOLS, ...STAGING_WRITE_TOOLS],
    maxInternalSubagents: 0,
    agentHints: ["planner"],
    skillHints: [],
  },
  {
    id: "testcase-v1",
    kind: "testcase",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook workflows/autopilot/testcase.md (skill test-driven-development)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} status*)",
      ...EDIT_FAMILY_TOOLS,
      "Bash(npm test*)",
      "Bash(pytest*)",
    ],
    maxInternalSubagents: 0,
    agentHints: [],
    skillHints: ["test-driven-development"],
    playbook: "workflows/autopilot/testcase.md",
  },
  {
    id: "implement-v1",
    kind: "implement",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook (agent fullstack-developer)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} status*)",
      "Bash(git -C {{WORKTREE}} diff*)",
      ...EDIT_FAMILY_TOOLS,
      "Bash(npm run build*)",
      "Bash(npm test*)",
      "Bash(pytest*)",
    ],
    maxInternalSubagents: 0,
    agentHints: ["fullstack-developer"],
    skillHints: [],
  },
  {
    id: "test-v1",
    kind: "test",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook workflows/autopilot/test.md",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(npm test*)",
      "Bash(pytest*)",
      // Typecheck before the suite (pack test.md step 1, agent tester MODE run):
      // without these the worker could only report "not run" for type errors.
      // Both are read-only checks; the recipe the controller re-runs is unchanged.
      "Bash(npm run typecheck*)",
      "Bash(deno check*)",
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} status*)",
    ],
    maxInternalSubagents: 0,
    agentHints: [],
    skillHints: [],
    playbook: "workflows/autopilot/test.md",
  },
  {
    id: "fix-v1",
    kind: "fix",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook (agent debugger + fullstack-developer)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} status*)",
      "Bash(git -C {{WORKTREE}} diff*)",
      ...EDIT_FAMILY_TOOLS,
      "Bash(npm test*)",
      "Bash(pytest*)",
    ],
    maxInternalSubagents: 1,
    agentHints: ["debugger", "fullstack-developer"],
    skillHints: [],
  },
  ...REVIEW_AND_CONTROLLER_ADAPTER_ROWS,
];
